import React, { useState } from 'react';
import { Text, StyleSheet } from 'react-native';
import AdminButton from '../../../Admin/components/AdminButton';
import AdminSelect from '../../../Admin/components/AdminSelect';
import { AdminFormField } from '../../../Admin/components/AdminFormField';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import ConfigCheckboxRow from '../components/ConfigCheckboxRow';
import ConfigDrawer from '../components/ConfigDrawer';
import { ConfigLocation, FocusArea, FocusOverrides } from '../configTypes';
import { locationKey } from '../configUtils';

interface FocusOverrideDrawerProps {
  org: string;
  dept: string;
  list: FocusArea[];
  /** Locations where the department is set up. */
  locations: ConfigLocation[];
  /** Set when editing an existing override row; absent for "Add Override". */
  startLoc?: string;
  overrides: FocusOverrides;
  onClose: () => void;
  onSave: (payload: { loc: string; off: string[]; initial: string[] }) => void;
}

/** Turns specific enabled national focus areas off for the department at one location. */
const FocusOverrideDrawer: React.FC<FocusOverrideDrawerProps> = ({
  org,
  dept,
  list,
  locations,
  startLoc,
  overrides,
  onClose,
  onSave,
}) => {
  const [loc, setLoc] = useState(startLoc ?? locations[0]?.name ?? '');
  const load = (l: string): string[] => overrides[locationKey(l, dept)]?.off ?? [];
  const [initial, setInitial] = useState<string[]>(() => load(loc));
  const [off, setOff] = useState<string[]>(initial);

  const pickLocation = (name: string) => {
    setLoc(name);
    setInitial(load(name));
    setOff(load(name));
  };

  const dirty = JSON.stringify([...off].sort()) !== JSON.stringify([...initial].sort());

  return (
    <ConfigDrawer
      title={startLoc ? 'Edit location override' : 'Add location override'}
      subtitle={`${loc} / ${dept} (${org})`}
      onClose={onClose}
      footer={
        <>
          <AdminButton label="Cancel" variant="secondary" onPress={onClose} />
          <AdminButton
            label="Save"
            variant="primary"
            disabled={!dirty}
            onPress={() => onSave({ loc, off, initial })}
          />
        </>
      }
    >
      {!startLoc ? (
        <AdminFormField label="Location">
          <AdminSelect
            value={loc}
            onChange={pickLocation}
            options={locations.map((l) => ({ value: l.name, label: l.name }))}
          />
        </AdminFormField>
      ) : null}
      <Text style={styles.note}>Uncheck a focus area to turn it off at this location.</Text>
      {list
        .filter((f) => f.enabled)
        .map((f) => (
          <ConfigCheckboxRow
            key={f.id}
            label={f.name}
            checked={!off.includes(f.id)}
            disabled={!!f.locked}
            onChange={(on) => setOff(on ? off.filter((x) => x !== f.id) : [...off, f.id])}
          />
        ))}
    </ConfigDrawer>
  );
};

const styles = StyleSheet.create({
  note: {
    fontSize: 12,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    marginBottom: 8,
  },
});

export default FocusOverrideDrawer;
